import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type {
  AnalysisComparisonResponseDto,
  AnalysisPercentilesResponseDto,
  CampaignAnalysisClient,
  CampaignAnalysisFilters,
  CampaignAnalysisMetaDto,
  CampaignStackReferentialDto,
  CampaignTargetPriceSummaryDto,
} from '@/api/campaign-analysis-client';
import { useCampaignAnalysisClient } from '@/api/campaign-analysis-client-context';

// =============================================================================
// use-campaign-analysis.ts — Hook TanStack Query du read-model ADMIN CampaignAnalysis (S17).
// =============================================================================
// RÉVISION G0-S17-17 : une SEULE lecture (percentiles consolidés ANONYMISÉS) en useQuery — le
// ranking nominatif détaillé est RETIRÉ (reporté S17-bis). Aucune mutation (admin LIT, jamais
// d'écriture, invariant n°1). Le client effectif vient du CampaignAnalysisClientProvider
// (doublable, stub-first). ZÉRO règle métier ici : les percentiles sont l'autorité de l'API/worker.
// Les filtres (F) entrent dans la queryKey pour un refetch au changement.

const CAMPAIGN_ANALYSIS_KEY = ['campaign-analysis'] as const;

/** Sérialise les filtres en clé stable (ordre déterministe) pour la queryKey. */
function filtersKey(filters: CampaignAnalysisFilters): readonly unknown[] {
  return [filters.profileCode ?? null, filters.seniorityLevel ?? null];
}

/** Clé de query des percentiles consolidés d'une campagne (selon filtres F). */
export function percentilesKey(
  campaignId: string,
  filters: CampaignAnalysisFilters,
): readonly unknown[] {
  return [...CAMPAIGN_ANALYSIS_KEY, 'percentiles', campaignId, ...filtersKey(filters)];
}

/** (B) GET percentiles consolidés winsorized (P10/P25/P50/P75/P90/MIN/MAX) par couple (ANONYMISÉ). */
export function useAnalysisPercentiles(
  campaignId: string,
  filters: CampaignAnalysisFilters,
): UseQueryResult<AnalysisPercentilesResponseDto, Error> {
  const client: CampaignAnalysisClient = useCampaignAnalysisClient();
  return useQuery<AnalysisPercentilesResponseDto, Error>({
    queryKey: percentilesKey(campaignId, filters),
    queryFn: () => client.getPercentiles(campaignId, filters),
  });
}

/** Clé de query des métadonnées d'analyse (computedAt, DBU §2.6 l.1345). */
export function analysisMetaKey(campaignId: string): readonly unknown[] {
  return [...CAMPAIGN_ANALYSIS_KEY, 'meta', campaignId];
}

/**
 * (B) V26-ANALYSE-UI — MÉTADONNÉES de l'analyse (GET /analysis, DÉJÀ exposé) : `computedAt` (date de calcul,
 * DBU §2.6 l.1345) + état + version de catalogue. SEULE source propre de la date (le contrat percentiles ne
 * porte pas de date, O2). Lecture SÉPARÉE (clé distincte) : l'écran corrèle date + rapport anonymisé.
 */
export function useCampaignAnalysisMeta(
  campaignId: string,
): UseQueryResult<CampaignAnalysisMetaDto, Error> {
  const client: CampaignAnalysisClient = useCampaignAnalysisClient();
  return useQuery<CampaignAnalysisMetaDto, Error>({
    queryKey: analysisMetaKey(campaignId),
    queryFn: () => client.getAnalysis(campaignId),
  });
}

/** Clé de query du référentiel « stack » (grain famille, DEC-17). */
export function stacksKey(campaignId: string): readonly unknown[] {
  return [...CAMPAIGN_ANALYSIS_KEY, 'stacks', campaignId];
}

/**
 * (B) V26-ANALYSE-UI / DEC-17 — RÉFÉRENTIEL « stack » (grain FAMILLE) du filtre d'analyse (GET /stacks).
 * Lecture d'affichage SÉPARÉE (§2.10 SpecificSkill + SkillFamilyAssoc côté API). L'UI peuple le filtre avec
 * `stacks` (familles ACTIVES) et tag chaque ligne par la famille de son profil (`profiles`).
 */
export function useCampaignStacks(
  campaignId: string,
): UseQueryResult<CampaignStackReferentialDto, Error> {
  const client: CampaignAnalysisClient = useCampaignAnalysisClient();
  return useQuery<CampaignStackReferentialDto, Error>({
    queryKey: stacksKey(campaignId),
    queryFn: () => client.getStacks(campaignId),
  });
}

// NUAGE DE POINTS — hooks SUPPRIMÉS (lot U1, ADR-FUSION-ANALYSE-RANG-01, décision 10 : « Le nuage de
// points est supprimé. »). Le composant, son mode d'affichage et son scénario système sont retirés ;
// `CampaignAnalysisClient.getPoints` reste exposé comme MIROIR de la route API (inchangée côté serveur),
// sans consommateur d'écran.

/** Clé de query du tableau de fin de campagne prix cible + médianes C1/C2 (L9). */
export function targetPriceSummaryKey(campaignId: string): readonly unknown[] {
  return [...CAMPAIGN_ANALYSIS_KEY, 'target-price-summary', campaignId];
}

/**
 * (B) L9 — TABLEAU DE FIN DE CAMPAGNE « Prix cible et médianes par cycle » (DBU §2.6, EF-BEN-12). Lecture
 * NOMINATIVE admin seule, clé distincte des percentiles anonymes. 404 = aucune analyse consolidée (erreur
 * `CampaignAnalysisApiError` status 404, état normal avant consolidation). `enabled` permet à l'écran de ne lire qu'une fois l'analyse disponible.
 */
export function useTargetPriceSummary(
  campaignId: string,
  enabled = true,
): UseQueryResult<CampaignTargetPriceSummaryDto, Error> {
  const client: CampaignAnalysisClient = useCampaignAnalysisClient();
  return useQuery<CampaignTargetPriceSummaryDto, Error>({
    queryKey: targetPriceSummaryKey(campaignId),
    queryFn: () => client.getTargetPriceSummary(campaignId),
    enabled,
  });
}

/** Clé de query de la comparaison campagnes antérieures (ids antérieurs triés = clé stable). */
export function analysisComparisonKey(
  campaignId: string,
  priorCampaignIds: readonly string[],
  filters: CampaignAnalysisFilters,
): readonly unknown[] {
  return [
    ...CAMPAIGN_ANALYSIS_KEY,
    'comparison',
    campaignId,
    [...priorCampaignIds].sort().join(','),
    ...filtersKey(filters),
  ];
}

/**
 * (B) GET comparaison avec les campagnes ANTÉRIEURES (V-ANALYSIS-COMPARE). `priorCampaignIds` = les
 * antérieures ÉLIGIBLES (même clientSiren, Publiee) résolues EN AMONT côté front. `enabled` GATE la lecture
 * (panneau optionnel masquable : on ne requête que lorsqu'il est ouvert ET qu'au moins une antérieure existe).
 */
export function useAnalysisComparison(
  campaignId: string,
  priorCampaignIds: readonly string[],
  filters: CampaignAnalysisFilters,
  enabled: boolean,
): UseQueryResult<AnalysisComparisonResponseDto, Error> {
  const client: CampaignAnalysisClient = useCampaignAnalysisClient();
  return useQuery<AnalysisComparisonResponseDto, Error>({
    queryKey: analysisComparisonKey(campaignId, priorCampaignIds, filters),
    queryFn: () =>
      client.getAnalysisComparison(campaignId, priorCampaignIds, filters),
    enabled,
  });
}
